// Pieces: a shorter session run inside the main session. Pure helpers.
// A piece points at its time window in the parent session
// (start_offset_s → end_offset_s); no raw data is copied.
export const DISTANCE_PRESETS = [500, 1000, 2000, 5000];
export const PIECES_STORAGE_KEY = 'crewz_pieces_v1';

export const newPieceId = () =>
  (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `pc_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`);

const fmtKm = (m) => (m >= 1000 ? `${+(m / 1000).toFixed(m % 1000 ? 1 : 0)} km` : `${Math.round(m)} m`);
const fmtMin = (s) => (s % 60 ? `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, '0')}` : `${Math.round(s / 60)} min`);
const fmtPace = (s) => `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, '0')}`;

// def: { type: 'distance'|'time'|'program'|'target', value?, targetPace?, program? }
export function pieceName(def) {
  if (def.type === 'distance') return fmtKm(def.value);
  if (def.type === 'time') return fmtMin(def.value);
  if (def.type === 'target') return `${fmtKm(def.value)} @ ${fmtPace(def.targetPace)}`;
  return def.program?.name || 'Program';
}

// snap: { elapsed (s), distance (m), strokes }
export function createPiece(def, number, snap) {
  return {
    piece_id: newPieceId(),
    piece_number: number,
    def,
    name: pieceName(def),
    start: { ...snap },
    intervalIdx: 0,
    intervalStart: { ...snap },
    srSum: 0,
    srN: 0,
  };
}

const intervalDone = (iv, since) =>
  (iv.distance ? since.distance >= iv.distance : since.time >= (iv.time || 0));

// Advance a piece to `snap`. Returns { piece, progress, done }.
export function advancePiece(piece, snap, strokeRate = 0) {
  const p = { ...piece, prevSnap: piece.lastSnap || piece.start, lastSnap: { elapsed: snap.elapsed, distance: snap.distance } };
  if (strokeRate > 0) { p.srSum += strokeRate; p.srN += 1; }
  const dist = Math.max(0, snap.distance - p.start.distance);
  const time = Math.max(0, snap.elapsed - p.start.elapsed);
  const { def } = p;
  let done = false;
  let fraction = 0;
  let interval = null;

  if (def.type === 'distance' || def.type === 'target') {
    done = dist >= def.value;
    fraction = Math.min(1, dist / def.value);
  } else if (def.type === 'time') {
    done = time >= def.value;
    fraction = Math.min(1, time / def.value);
  } else {
    const ivs = def.program?.intervals || [];
    let since = { distance: snap.distance - p.intervalStart.distance, time: snap.elapsed - p.intervalStart.elapsed };
    while (p.intervalIdx < ivs.length && intervalDone(ivs[p.intervalIdx], since)) {
      p.intervalIdx += 1;
      p.intervalStart = { ...snap };
      since = { distance: 0, time: 0 };
    }
    done = p.intervalIdx >= ivs.length;
    fraction = ivs.length ? Math.min(1, p.intervalIdx / ivs.length) : 1;
    if (!done) {
      const iv = ivs[p.intervalIdx];
      const remaining = iv.distance ? Math.max(0, iv.distance - since.distance) : Math.max(0, (iv.time || 0) - since.time);
      interval = { index: p.intervalIdx, count: ivs.length, ...iv, remaining, unit: iv.distance ? 'm' : 's' };
    }
  }
  return { piece: p, done, progress: { distance: dist, time, fraction, interval } };
}

export function summarizePiece(piece, snap, reason) {
  const { def } = piece;
  // Auto-ended distance pieces are capped at their target (sample overshoot)
  let distance = Math.max(0, snap.distance - piece.start.distance);
  let endElapsed = snap.elapsed;
  if (reason === 'auto' && (def.type === 'distance' || def.type === 'target') && distance > def.value) {
    // Interpolate the moment the target distance was crossed (≈1 Hz samples overshoot)
    const prev = piece.prevSnap || piece.start;
    const d0 = prev.distance - piece.start.distance;
    if (distance > d0 && snap.elapsed > prev.elapsed) {
      endElapsed = prev.elapsed + ((def.value - d0) / (distance - d0)) * (snap.elapsed - prev.elapsed);
    }
    distance = def.value;
  }
  const time = Math.max(0, endElapsed - piece.start.elapsed);
  return {
    piece_id: piece.piece_id,
    piece_number: piece.piece_number,
    piece_type: def.type,
    piece_name: piece.name,
    target_value: def.type === 'program' ? null : def.value,
    target_pace_s: def.type === 'target' ? def.targetPace : null,
    program_id: def.type === 'program' ? (def.program?.program_id || def.program?.id || null) : null,
    start_offset_s: piece.start.elapsed,
    end_offset_s: Math.round(endElapsed * 10) / 10,
    distance_m: Math.round(distance),
    time_s: Math.round(time * 10) / 10,
    avg_split_s: distance > 0 ? Math.round((time / (distance / 500)) * 10) / 10 : null,
    avg_stroke_rate: piece.srN ? Math.round((piece.srSum / piece.srN) * 10) / 10 : null,
    stroke_count: Math.max(0, (snap.strokes || 0) - (piece.start.strokes || 0)),
    ended_reason: reason,
  };
}

// Cox voice cues: start, halfway, last 250 m (distance/target pieces), done.
// Returns { cues: [text], fired } — `fired` holds keys already spoken.
export function pieceCues(piece, progress, fired = {}) {
  const out = [];
  const next = { ...fired };
  const { def } = piece;
  const say = (key, text) => { if (!next[key]) { next[key] = true; out.push(text); } };
  if (!progress) return { cues: out, fired: next };
  const isDist = def.type === 'distance' || def.type === 'target';
  const remaining = isDist ? def.value - progress.distance : null;
  const halfAlsoLast = isDist && def.value <= 500;
  if (progress.fraction >= 0.5 && def.type !== 'program') {
    say('half', halfAlsoLast ? 'Halfway. Last two fifty, build now!' : 'Halfway. Hold it together, stay long.');
    if (halfAlsoLast) next.last250 = true;
  }
  if (isDist && def.value > 500 && remaining <= 250) say('last250', 'Last two fifty! Empty the tank!');
  return { cues: out, fired: next };
}

export const pieceStartCue = (piece) => `Piece ${piece.piece_number}. ${piece.name}. Go!`;
export const pieceEndCue = (r) => `Piece ${r.piece_number} done. Nice work.`;
